import { isCustomerOrderCloseableByPaidQty } from '#/modules/crmMgmt/customerOrder/helper/customerOrderCloseEligibility';

/** 客戶訂單狀態（契約 7 態；未知態 label／badge 不炸）。 */

export const CUSTOMER_ORDER_STATUS = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  APPROVED: 'APPROVED',
  PARTIALLY_SHIPPED: 'PARTIALLY_SHIPPED',
  SHIPPED: 'SHIPPED',
  CLOSED: 'CLOSED',
  TERMINATED: 'TERMINATED',
};

export const CUSTOMER_ORDER_STATUS_LABELS = {
  DRAFT: '草稿',
  SUBMITTED: '已提交',
  APPROVED: '已核准',
  PARTIALLY_SHIPPED: '部分出貨',
  SHIPPED: '已出貨',
  CLOSED: '已結案',
  TERMINATED: '已中止',
};

export const CUSTOMER_ORDER_STATUS_FILTER_OPTIONS = [
  { value: 'DRAFT', label: '草稿' },
  { value: 'SUBMITTED', label: '已提交' },
  { value: 'APPROVED', label: '已核准' },
  { value: 'PARTIALLY_SHIPPED', label: '部分出貨' },
  { value: 'SHIPPED', label: '已出貨' },
  { value: 'CLOSED', label: '已結案' },
  { value: 'TERMINATED', label: '已中止' },
];

/** 寫死色票：深底淺字 */
export const CUSTOMER_ORDER_STATUS_CLASS = {
  DRAFT: 'border-transparent bg-slate-600 text-slate-50',
  SUBMITTED: 'border-transparent bg-sky-600 text-sky-50',
  APPROVED: 'border-transparent bg-amber-600 text-amber-50',
  PARTIALLY_SHIPPED: 'border-transparent bg-violet-600 text-violet-50',
  SHIPPED: 'border-transparent bg-teal-600 text-teal-50',
  CLOSED: 'border-transparent bg-emerald-600 text-emerald-50',
  TERMINATED: 'border-transparent bg-rose-600 text-rose-50',
};

export function formatCustomerOrderStatusLabel(status) {
  if (status == null || status === '') {
    return '-';
  }
  return CUSTOMER_ORDER_STATUS_LABELS[status] ?? status;
}

function isPostApproveShipStatus(status) {
  return (
    status === CUSTOMER_ORDER_STATUS.APPROVED
    || status === CUSTOMER_ORDER_STATUS.PARTIALLY_SHIPPED
    || status === CUSTOMER_ORDER_STATUS.SHIPPED
  );
}

/** 僅 DRAFT 可進 Create／Edit 全單編輯。 */
export function canEditCustomerOrderDraft(status) {
  return status === CUSTOMER_ORDER_STATUS.DRAFT;
}

export function canSubmitCustomerOrder(status) {
  return status === CUSTOMER_ORDER_STATUS.DRAFT;
}

export function canDeleteDraftCustomerOrder(status) {
  return status === CUSTOMER_ORDER_STATUS.DRAFT;
}

export function canApproveCustomerOrder(status) {
  return status === CUSTOMER_ORDER_STATUS.SUBMITTED;
}

export function canRejectCustomerOrder(status) {
  return status === CUSTOMER_ORDER_STATUS.SUBMITTED;
}

/**
 * 可結案：核准後出貨態，且各產品 PAID SO qty ≥ order_qty − cancel_qty。
 * @param {string} status
 * @param {object} [detail] 有 Detail 時一併驗 PAID qty；缺 Detail 時僅看狀態（按鈕可露、成敗交後端）
 */
export function canCloseCustomerOrder(status, detail) {
  if (!isPostApproveShipStatus(status)) {
    return false;
  }
  if (detail == null) {
    return true;
  }
  return isCustomerOrderCloseableByPaidQty(detail);
}

export function canTerminateCustomerOrder(status) {
  return isPostApproveShipStatus(status);
}

/** SUBMITTED 起可改備註（dedicated remark API／頁）；DRAFT 除外。 */
/** 已結案客訂重新開啟（沖銷／退款前）；後端還原結案前狀態。 */
export function canReopenCustomerOrder(status) {
  return status === CUSTOMER_ORDER_STATUS.CLOSED;
}

export function canUpdateCustomerOrderRemark(status) {
  return Boolean(status) && status !== CUSTOMER_ORDER_STATUS.DRAFT;
}

/**
 * 可維護交期排程：DRAFT／APPROVED／PARTIALLY_SHIPPED／SHIPPED。
 * SUBMITTED／CLOSED／TERMINATED 不可。
 */
export function canEditCustomerOrderSchedule(status) {
  return (
    status === CUSTOMER_ORDER_STATUS.DRAFT
    || status === CUSTOMER_ORDER_STATUS.APPROVED
    || status === CUSTOMER_ORDER_STATUS.PARTIALLY_SHIPPED
    || status === CUSTOMER_ORDER_STATUS.SHIPPED
  );
}

/**
 * 可自客訂建立銷貨單：APPROVED／PARTIALLY_SHIPPED／SHIPPED。
 */
export function canCreateSalesOrderFromCustomerOrder(status) {
  return isPostApproveShipStatus(status);
}

/**
 * 可建立多階層工單：僅 APPROVED（已核准）。
 */
export function canCreateManufacturingWorkOrdersFromCustomerOrder(status) {
  return status === CUSTOMER_ORDER_STATUS.APPROVED;
}

/** @deprecated 請改用 canCreateSalesOrderFromCustomerOrder */
export const canCreateSalesOrderFromSchedule = canCreateSalesOrderFromCustomerOrder;

/** 短別名（對齊 workflow 表／SP 文案）。 */
export const canEditDraft = canEditCustomerOrderDraft;
export const canSubmit = canSubmitCustomerOrder;
export const canDeleteDraft = canDeleteDraftCustomerOrder;
export const canApprove = canApproveCustomerOrder;
export const canReject = canRejectCustomerOrder;
export const canClose = canCloseCustomerOrder;
export const canTerminate = canTerminateCustomerOrder;
export const canEditSchedule = canEditCustomerOrderSchedule;
